import { useEffect } from 'react';
import { toast } from 'react-toastify';

// A tab keeps running the version it loaded, even after the server has been
// updated. The server reports (in /health) the page version it was started
// with; when that differs from the one this tab runs, offer a reload. Using
// the running server's version (not index.html on disk) means the reload is
// only offered once the new server code is running too.
const scriptOf = (html) => (html.match(/\/static\/js\/main\.[0-9a-f]+\.js/) || [])[0];
const CHECK_EVERY_MS = 5 * 60 * 1000;

const useNewVersionCheck = () => {
    useEffect(() => {
        const current = scriptOf(document.documentElement.innerHTML);
        if (!current) return undefined; // development server: nothing to compare

        let notified = false;
        const check = async () => {
            if (notified || document.hidden) return;
            try {
                const res = await fetch('/health', { cache: 'no-store' });
                const live = res.ok && (await res.json()).build;
                if (live && live !== current) {
                    notified = true;
                    toast.info('A new version of the system is available. Click here to reload.', {
                        toastId: 'new-version',
                        autoClose: false,
                        closeOnClick: true,
                        onClick: () => window.location.reload(),
                    });
                }
            } catch {
                // Offline or server restarting: try again next time.
            }
        };

        check();
        const timer = setInterval(check, CHECK_EVERY_MS);
        window.addEventListener('focus', check);
        document.addEventListener('visibilitychange', check);
        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', check);
            document.removeEventListener('visibilitychange', check);
        };
    }, []);
};

export default useNewVersionCheck;
